/**
 * E06-F05-T15 — technical ("raw") research blocks: one label, one visibility setting.
 *
 * The 08-10-26 sync with Ksenia: "Search results by tile", the collected-sources
 * drawer ("exactly what was sent to the model") and the Sources button mean nothing
 * to a recruiter. They stay for now — they are how the research algorithm is being
 * debugged — but they are marked as technical, collapsed by default, and can be
 * hidden from non-admins with ONE switch once the algorithm is validated.
 *
 * THE switch: `VITE_RESEARCH_RAW_DETAILS_AUDIENCE` (build-time, Vite env) —
 *   - `everyone` (default) — every role sees the collapsed raw blocks;
 *   - `admins`             — only `role === 'admin'` sees them; for everyone else
 *                            the blocks (and the Raw sources button) are not rendered.
 * Any other value falls back to `everyone` (never silently hides debugging data).
 *
 * The role comes from the session store (`getCachedUser`), which `useAuth` keeps
 * equal to the confirmed principal; research screens only render when authenticated.
 * This is a UI-visibility switch, not an access control: the underlying API data is
 * not secret (same request the user already owns).
 */
import { getCachedUser } from '../api/session.js';

/** Explicit label of every technical research block (acceptance #1). */
export const RAW_DETAILS_LABEL = 'Technical details (raw)';

export type RawDetailsAudience = 'everyone' | 'admins';

/** Parse the env value; anything unknown keeps the blocks visible. Pure. */
export function parseRawDetailsAudience(value: unknown): RawDetailsAudience {
  return typeof value === 'string' && value.trim().toLowerCase() === 'admins'
    ? 'admins'
    : 'everyone';
}

/** The one setting (acceptance #2). Flip to `admins` after the algorithm is validated. */
export const RESEARCH_RAW_DETAILS_AUDIENCE: RawDetailsAudience = parseRawDetailsAudience(
  import.meta.env?.VITE_RESEARCH_RAW_DETAILS_AUDIENCE,
);

/** Whether a user with `role` may see the raw research blocks. Pure. */
export function canSeeRawDetails(
  role: string | null | undefined,
  audience: RawDetailsAudience = RESEARCH_RAW_DETAILS_AUDIENCE,
): boolean {
  return audience === 'everyone' || role === 'admin';
}

/** Hook form for components (reads the signed-in principal's role). */
export function useCanSeeRawDetails(): boolean {
  return canSeeRawDetails(getCachedUser()?.role ?? null);
}
